/* Created by Interactive 63 - https://interactive63.com */
/**
 * Artboard arithmetic: resizing around an anchor, dragging an edge or corner, fitting to the
 * artwork and the size presets. The artboard is the SVG's view box, so every function takes and
 * returns a box in the artwork's own units. The artwork never moves; only the frame around it does.
 */

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Which part of the frame stays put: 0 is the left or top, 0.5 the middle, 1 the right or bottom. */
export interface Anchor {
  x: number;
  y: number;
}

export type ArtboardHandle = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';

export interface DragOptions {
  /** Resize from the middle, so both sides move. */
  fromCentre?: boolean;
  /** Keep the proportions on corner handles. */
  keepRatio?: boolean;
}

export const ARTBOARD_MIN_SIDE = 8;
export const ARTBOARD_MAX_SIDE = 16384;
const POSITION_DECIMALS = 2;

export const ARTBOARD_PRESETS: { label: string; width: number; height: number }[] = [
  { label: 'Square (1080 × 1080)', width: 1080, height: 1080 },
  { label: 'Landscape 16:9 (1920 × 1080)', width: 1920, height: 1080 },
  { label: 'Portrait 9:16 (1080 × 1920)', width: 1080, height: 1920 },
  { label: 'Standard 4:3 (1600 × 1200)', width: 1600, height: 1200 },
  { label: 'Banner (1500 × 500)', width: 1500, height: 500 },
  { label: 'Icon (512 × 512)', width: 512, height: 512 },
];

/** Rounds a position to a sensible precision. */
function roundPosition(value: number): number {
  const factor = 10 ** POSITION_DECIMALS;
  return Math.round(value * factor) / factor;
}

/** Rounds a side length to a whole unit inside the allowed range. */
export function clampSide(length: number): number {
  if (!Number.isFinite(length)) return ARTBOARD_MIN_SIDE;
  return Math.min(ARTBOARD_MAX_SIDE, Math.max(ARTBOARD_MIN_SIDE, Math.round(length)));
}

/** Gives the box a new size, keeping the anchored point of the frame where it was. */
export function resizeAround(box: Box, width: number, height: number, anchor: Anchor): Box {
  const w = clampSide(width);
  const h = clampSide(height);
  return {
    x: roundPosition(box.x + (box.width - w) * anchor.x),
    y: roundPosition(box.y + (box.height - h) * anchor.y),
    width: w,
    height: h,
  };
}

/** The direction a handle pulls in on each axis: -1 for the near side, 1 for the far side. */
function pull(handle: ArtboardHandle): { x: number; y: number } {
  return {
    x: handle.includes('e') ? 1 : handle.includes('w') ? -1 : 0,
    y: handle.includes('s') ? 1 : handle.includes('n') ? -1 : 0,
  };
}

/**
 * The box after dragging a handle by a distance in artwork units. The side opposite the handle
 * stays put, or the middle does when resizing from the centre.
 */
export function dragHandle(
  start: Box,
  handle: ArtboardHandle,
  dx: number,
  dy: number,
  options: DragOptions = {},
): Box {
  const side = pull(handle);
  const factor = options.fromCentre ? 2 : 1;
  let width = start.width + side.x * dx * factor;
  let height = start.height + side.y * dy * factor;
  if (options.keepRatio && side.x !== 0 && side.y !== 0) {
    const ratio = start.width / start.height;
    const widthChange = Math.abs(width / start.width - 1);
    const heightChange = Math.abs(height / start.height - 1);
    if (widthChange >= heightChange) height = width / ratio;
    else width = height * ratio;
  }
  const anchor: Anchor = options.fromCentre
    ? { x: 0.5, y: 0.5 }
    : {
        x: side.x === 1 ? 0 : side.x === -1 ? 1 : 0.5,
        y: side.y === 1 ? 0 : side.y === -1 ? 1 : 0.5,
      };
  return resizeAround(start, width, height, anchor);
}

/** The smallest whole-unit box that holds some artwork bounds, which may be fractional. */
export function boundsToArtboard(bounds: Box): Box {
  const left = Math.floor(bounds.x);
  const top = Math.floor(bounds.y);
  const right = Math.ceil(bounds.x + bounds.width);
  const bottom = Math.ceil(bounds.y + bounds.height);
  return {
    x: left,
    y: top,
    width: clampSide(right - left),
    height: clampSide(bottom - top),
  };
}
